import React, { useState, useEffect, useCallback } from "react";
import { Icon } from "semantic-ui-react";
import { toast } from "react-toastify";
import { Inspection, Industry as IndustryApi, ChecklistNormReference } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { NORM_COUNTRIES } from "../normCountries";
import "./ChecklistTemplateForm.scss";

const inspectionCtrl = new Inspection();
const industryCtrl   = new IndustryApi();
const normRefCtrl     = new ChecklistNormReference();

// Valores semilla para categoría de ítem — admin-extensible vía datalist
const ITEM_CATEGORIES = [
  "Orden y Limpieza",
  "Señalética y vías de evacuación",
  "Equipos contra incendio / extintores",
  "Protecciones de máquinas y equipos",
  "Instalaciones eléctricas",
  "Almacenamiento y estanterías",
  "EPP disponible y en condiciones",
  "Iluminación y ventilación",
  "Gestión de residuos",
  "Herramientas manuales y eléctricas",
  "Condiciones edilicias / infraestructura",
  "Documentación visible (habilitaciones, planos)",
  "Ergonomía de puestos",
  "Otro",
];

const EMPTY_ITEM = () => ({
  _key: Math.random().toString(36).slice(2),
  question: "",
  category: "",
  required: true,
  requirePhotoOnNonCompliant: false,
});

function ItemRow({ item, onChange, onRemove }) {
  return (
    <div className="ctf__item">
      <div className="ctf__item-row">
        <input
          className="ctf__item-input ctf__item-input--question"
          placeholder="Pregunta / Criterio de evaluación"
          value={item.question}
          onChange={(e) => onChange({ question: e.target.value })}
        />
        <select
          className="ctf__item-input ctf__item-input--cat"
          value={item.category}
          onChange={(e) => onChange({ category: e.target.value })}
        >
          <option value="">Categoría</option>
          {ITEM_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button className="ctf__remove-btn" onClick={onRemove} title="Eliminar ítem">
          <svg width="13" height="12" viewBox="0 0 13 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1L12 11M12 1L1 11" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      </div>
      <div className="ctf__item-checks">
        <label className="ctf__check-label">
          <input
            type="checkbox"
            checked={item.required}
            onChange={(e) => onChange({ required: e.target.checked })}
          />
          Obligatorio
        </label>
        <label className="ctf__check-label">
          <input
            type="checkbox"
            checked={item.requirePhotoOnNonCompliant}
            onChange={(e) => onChange({ requirePhotoOnNonCompliant: e.target.checked })}
          />
          Foto requerida si No Cumple
        </label>
      </div>
    </div>
  );
}

export function ChecklistTemplateForm({ template, initialDerivationMode, country, close, onSaved }) {
  const { accessToken } = useAuth();
  const isEdit = Boolean(template?._id);

  const toItemState = (items = []) =>
    items.map((it) => ({
      _key:                      it._id || Math.random().toString(36).slice(2),
      _id:                       it._id,
      question:                  it.question || "",
      category:                  it.category || "",
      required:                  it.required !== false,
      requirePhotoOnNonCompliant: it.requirePhotoOnNonCompliant || false,
    }));

  const [name,           setName]           = useState(template?.name || "");
  const [description,    setDescription]    = useState(template?.description || "");
  const [category,       setCategory]       = useState(template?.category || "");
  const [industryId,     setIndustryId]     = useState(template?.industry?._id || template?.industry || "");
  // El modo de derivación ya viene decidido desde el botón "Crear checklist
  // propio" (Manual/Automática, ver NewChecklistButton) — o desde la
  // plantilla existente si se está editando. Acá se muestra fijo, no como
  // toggle: en Figma son dos modales distintos para cada modo.
  const derivationMode = template?.derivationMode || initialDerivationMode || "manual";
  const [items,          setItems]          = useState(() => toItemState(template?.items || []));
  const [saving,         setSaving]         = useState(false);
  const [industries,     setIndustries]     = useState([]);
  const [normRefs,       setNormRefs]       = useState([]);

  useEffect(() => {
    industryCtrl.getIndustries(accessToken)
      .then((res) => setIndustries(Array.isArray(res) ? res : []))
      .catch(() => {});
  }, [accessToken]);

  // Norma de referencia (país actual) para el "Datos generales" — solo
  // existe para una plantilla ya guardada (se carga desde Catálogos). Una
  // plantilla nueva todavía no tiene _id, así que acá no hay nada que
  // buscar y no se muestra nada.
  useEffect(() => {
    if (!isEdit) return;
    normRefCtrl.getNormReferences(accessToken)
      .then((res) => setNormRefs(Array.isArray(res) ? res : []))
      .catch(() => setNormRefs([]));
  }, [accessToken, isEdit]);

  const norm = isEdit && country
    ? normRefs.find((n) => (n.template?._id || n.template) === template._id && n.country === country && n.active !== false)
    : null;
  const normCountryText = norm ? (NORM_COUNTRIES.find((c) => c.value === norm.country)?.text || norm.country) : null;

  const addItem = () => setItems((prev) => [...prev, EMPTY_ITEM()]);

  const updateItem = useCallback((key, patch) => {
    setItems((prev) => prev.map((it) => it._key === key ? { ...it, ...patch } : it));
  }, []);

  const removeItem = useCallback((key) => {
    setItems((prev) => prev.filter((it) => it._key !== key));
  }, []);

  const validate = () => {
    if (!name.trim()) { toast.error("El nombre de la plantilla es obligatorio."); return false; }
    if (items.length === 0) { toast.error("La plantilla debe tener al menos un ítem."); return false; }
    for (let i = 0; i < items.length; i++) {
      if (!items[i].question.trim()) {
        toast.error(`El ítem ${i + 1} no tiene pregunta.`);
        return false;
      }
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const body = {
        name:           name.trim(),
        description:    description.trim(),
        category:       category.trim(),
        industry:       industryId || undefined,
        derivationMode,
        items: items.map((it, idx) => ({
          ...(it._id ? { _id: it._id } : {}),
          question:                  it.question.trim(),
          category:                  it.category.trim(),
          required:                  it.required,
          requirePhotoOnNonCompliant: it.requirePhotoOnNonCompliant,
          order:                     idx,
        })),
      };
      if (isEdit) {
        await inspectionCtrl.updateTemplate(accessToken, template._id, body);
      } else {
        await inspectionCtrl.createTemplate(accessToken, body);
      }
      onSaved?.();
    } catch (err) {
      toast.error(err?.message || "Error al guardar la plantilla.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ctf">
      {/* Header — el botón de cerrar ya lo pone BasicModal (closeIcon) */}
      <div className="ctf__header">
        <span className="ctf__header-title">
          {isEdit
            ? "Editar checklist propio"
            : derivationMode === "auto"
              ? "Nuevo checklist automático"
              : "Nuevo checklist manual"}
        </span>
      </div>

      <div className="ctf__body">
        {/* Datos generales */}
        <section className="ctf__section">
          <div className="ctf__section-title">
            <span>Datos generales</span>
            {norm && (
              <span className="ctf__norm-ref">
                <strong>{normCountryText}:</strong> {norm.norm}
              </span>
            )}
          </div>

          <div className="ctf__field">
            <label className="ctf__label">Nombre <span className="ctf__req">*</span></label>
            <input
              className="ctf__input"
              placeholder="Ej: Inspección de EPP"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="ctf__field">
            <label className="ctf__label">Descripción</label>
            <input
              className="ctf__input"
              placeholder="Breve descripción del alcance"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="ctf__field">
            <label className="ctf__label">Industria / Sector</label>
            <select
              className="ctf__input ctf__input--select"
              value={industryId}
              onChange={(e) => setIndustryId(e.target.value)}
            >
              <option value="">— Sin industria asignada —</option>
              {industries.map((ind) => (
                <option key={ind._id} value={ind._id}>{ind.name}</option>
              ))}
            </select>
          </div>
          <div className="ctf__field">
            <label className="ctf__label">Categoría de plantilla</label>
            <input
              className="ctf__input"
              placeholder="Ej: Seguridad, Ambiente, EPP"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            />
          </div>

          {/* Card blanca — el modo ya viene fijo desde el botón "Crear
              checklist propio" (Manual/Automática), acá solo se informa. */}
          <div className="ctf__derivation-block">
            <div className="ctf__derivation-block-text">
              <span className="ctf__derivation-block-title">Derivación de hallazgos</span>
              <span className="ctf__derivation-block-hint">
                {derivationMode === "auto"
                  ? "Las CAPAs se crean automáticamente al completar la inspección."
                  : "El inspector decide qué hallazgos derivan a CAPA."}
              </span>
            </div>
            <span className="ctf__derivation-pill">
              {derivationMode === "auto" ? "Automática" : "Manual"}
            </span>
          </div>
        </section>

        {/* Ítems */}
        <section className="ctf__section">
          <div className="ctf__items-header">
            <h4 className="ctf__section-title">Ítems de evaluación</h4>
            <button className="ctf__add-item-btn" onClick={addItem}>
              Agregar ítem
            </button>
          </div>

          {items.length > 0 && (
            <div className="ctf__items-list">
              {items.map((item) => (
                <ItemRow
                  key={item._key}
                  item={item}
                  onChange={(patch) => updateItem(item._key, patch)}
                  onRemove={() => removeItem(item._key)}
                />
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Footer */}
      <div className="ctf__footer">
        <button className="ctf__btn ctf__btn--cancel" onClick={close} disabled={saving}>
          Cancelar
        </button>
        <button className="ctf__btn ctf__btn--save" onClick={handleSave} disabled={saving}>
          {saving ? <><Icon name="spinner" loading /> Guardando…</> : isEdit ? "Guardar cambios" : "Crear plantilla"}
        </button>
      </div>
    </div>
  );
}
